import { useState, useEffect, useCallback } from 'react';
import CasesAPI from '../../../axios/case/CasesApi';
import PhysicianAPI from '../../../axios/physician/PhysicianApi';

const LIMIT = 20;

const getTodayString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const getDefaultFilters = () => ({
    completedAtStart: getTodayString(),
    completedAtEnd: getTodayString(),
    categoryId: '',
    providerId: '',
    requestType: '',
});

const useCEVLogs = () => {
    const [filters, setFilters] = useState(getDefaultFilters);

    const [categories, setCategories] = useState([]);
    const [providers, setProviders] = useState([]);
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [providersLoading, setProvidersLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const fetchCategories = useCallback(async () => {
        setCategoriesLoading(true);
        try {
            const response = await CasesAPI.getCEVCategories();
            setCategories(response.data?.data || []);
        } catch (err) {
            console.error('Failed to fetch CEV categories:', err);
        } finally {
            setCategoriesLoading(false);
        }
    }, []);

    const fetchProviders = useCallback(async () => {
        setProvidersLoading(true);
        try {
            const response = await PhysicianAPI.getPhysicians({ active: true });
            const list = response.data?.payload || response.data?.physicians || [];
            const sorted = [...list].sort((a, b) =>
                `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
            );
            setProviders(sorted);
        } catch (err) {
            console.error('Failed to fetch providers:', err);
        } finally {
            setProvidersLoading(false);
        }
    }, []);

    const fetchLogs = useCallback(async (currentPage = 1, currentFilters = filters) => {
        setLoading(true);
        setHasSearched(true);
        try {
            const params = {
                page: currentPage,
                limit: LIMIT,
                ...(currentFilters.completedAtStart && { completedAtStart: currentFilters.completedAtStart }),
                ...(currentFilters.completedAtEnd && { completedAtEnd: currentFilters.completedAtEnd }),
                ...(currentFilters.categoryId && { categoryId: currentFilters.categoryId }),
                ...(currentFilters.providerId && { providerId: currentFilters.providerId }),
                ...(currentFilters.requestType && { requestType: currentFilters.requestType }),
            };
            const response = await CasesAPI.getCEVLogs(params);
            const { data = [], total: totalCount = 0 } = response.data || {};
            setLogs(data);
            setTotal(totalCount);
            setPage(currentPage);
        } catch (err) {
            console.error('Failed to fetch CEV logs:', err);
            setLogs([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const handleFilterChange = useCallback((key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleSearch = useCallback(() => {
        fetchLogs(1, filters);
    }, [fetchLogs, filters]);

    const handlePageChange = useCallback((newPage) => {
        fetchLogs(newPage, filters);
    }, [fetchLogs, filters]);

    const handleReset = useCallback(() => {
        const resetFilters = getDefaultFilters();
        setFilters(resetFilters);
        fetchLogs(1, resetFilters);
    }, [fetchLogs]);

    useEffect(() => {
        fetchCategories();
        fetchProviders();
    }, [fetchCategories, fetchProviders]);

    useEffect(() => {
        fetchLogs(1, filters);
        // Only run on mount — filters is stable at this point
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        filters,
        categories,
        providers,
        logs,
        total,
        page,
        limit: LIMIT,
        loading,
        categoriesLoading,
        providersLoading,
        hasSearched,
        handleFilterChange,
        handleSearch,
        handlePageChange,
        handleReset,
    };
};

export default useCEVLogs;
